import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DocumentUploadPage = () => {
  // State variables
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [documents, setDocuments] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchDocuments();
    } else {
      setLoading(false);
      setMessage('Please log in to view documents');
    }
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/documents');
      console.log('Documents API response:', res.data);
      
      // Handle different response formats
      if (Array.isArray(res.data)) {
        setDocuments(res.data);
      } else if (res.data.documents && Array.isArray(res.data.documents)) {
        setDocuments(res.data.documents);
      } else {
        console.error('Unexpected API response format:', res.data);
        setDocuments([]);
        setMessage('Warning: Unable to load documents list');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setDocuments([]);
      if (err.response?.status === 401) {
        setMessage('Session expired. Please log in again.');
      } else {
        setMessage(`Error loading documents: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setMessage('');
    
    // Show preview for images
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    setMessage('');
    
    try {
      // Standard upload with reliable OCR
      const formData = new FormData();
      formData.append('document', file);
      
      const response = await axios.post('/api/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessage('Document uploaded and processed successfully!');
      setFile(null);
      setImagePreview(null);
      document.getElementById('document').value = '';
      
      // Refresh the documents list
      await fetchDocuments();
      
    } catch (err) {
      console.error('Upload error:', err);
      if (err.response?.status === 413) {
        setMessage('Error: File too large! Maximum file size is 200MB. Please choose a smaller file.');
      } else if (err.response?.status === 401) {
        setMessage('Session expired. Please log in again.');
      } else {
        setMessage(`Error: ${err.message || err.response?.data?.error || 'Failed to process document'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, originalName) => {
    if (!window.confirm(`Are you sure you want to delete "${originalName}"?`)) {
      return;
    }

    setDeleting(docId);
    try {
      await axios.delete(`/api/documents/${docId}`);
      setMessage('Document deleted successfully!');
      fetchDocuments(); // Refresh the list
    } catch (err) {
      console.error('Delete error:', err);
      if (err.response?.status === 401) {
        setMessage('Session expired. Please log in again.');
      } else {
        setMessage(`Error: ${err.response?.data?.error || 'Failed to delete document'}`);
      }
    } finally {
      setDeleting(null);
    }
  };
  
  const isImageDocument = (doc) => {
    return doc.mimetype && doc.mimetype.startsWith('image/');
  };
  
  const handleViewImage = (doc) => {
    setViewingImage(doc);
  };
  
  const closeImageViewer = () => {
    setViewingImage(null);
  };

  if (loading) {
    return (
      <div style={{maxWidth: '900px', margin: '0 auto', padding: '2rem', textAlign: 'center'}}>
        <h1>Loading Documents...</h1>
      </div>
    );
  }

  return (
    <div style={{maxWidth: '900px', margin: '0 auto', padding: '2rem'}}>
      <h1>Document Management</h1>
      <p style={{marginBottom: '2rem'}}>Upload genealogical records for GNology Powered analysis by Gene</p>
      
      <div style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '2rem'}}>
        {message && (
          <p style={{
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            background: message.startsWith('Error') || message.includes('expired') || message.includes('Warning') ? '#f8d7da' : '#d4edda',
            color: message.startsWith('Error') || message.includes('expired') || message.includes('Warning') ? '#721c24' : '#155724',
            border: `1px solid ${message.startsWith('Error') || message.includes('expired') || message.includes('Warning') ? '#f5c6cb' : '#c3e6cb'}`
          }}>
            {message}
          </p>
        )}

        {/* Honest OCR Information */}
        <div style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e8f5e9 100%)', 
          padding: '1.5rem', 
          borderRadius: '10px', 
          marginBottom: '1.5rem',
          border: '2px solid #2e7d32',
          boxShadow: '0 2px 8px rgba(46, 125, 50, 0.1)'
        }}>
          <div style={{display: 'flex', alignItems: 'center', marginBottom: '1rem'}}>
            <span style={{fontSize: '1.5rem', marginRight: '0.5rem'}}>📄</span>
            <h3 style={{margin: 0, color: '#2e7d32'}}>Professional OCR Processing</h3>
          </div>
          
          <div style={{
            fontSize: '0.95rem', 
            color: '#666', 
            lineHeight: '1.4'
          }}>
            🎯 <strong>High-quality text extraction</strong> for genealogical documents<br/>
            🌍 <strong>Multi-language support</strong> including European languages common in genealogy<br/>
            ✍️ <strong>Effective handwriting processing</strong> optimized for historical documents<br/>
            ⚡ <strong>Reliable processing</strong> with accurate text extraction
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '1.5rem'}}>
            <label htmlFor="document" style={{display: 'block', marginBottom: '0.5rem', fontWeight: '500'}}>
              Select Document
            </label>
            <input 
              type="file"
              id="document"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
              style={{display: 'block', width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px'}}
            />
            <p style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>
              Accepted formats: PDF, JPG, PNG, DOC, DOCX, TXT (Max size: 200MB)
            </p>
          </div>
          
          {/* Image Preview */}
          {imagePreview && (
            <div style={{marginBottom: '1.5rem'}}>
              <p style={{fontWeight: '500', marginBottom: '0.5rem'}}>Preview:</p>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{
                  maxWidth: '100%', 
                  maxHeight: '300px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  objectFit: 'contain'
                }} 
              />
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem',
                background: '#e3f2fd',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}>
                📄 This document will be processed with professional OCR for accurate text extraction
              </div>
            </div>
          )}
          
          <button 
            type="submit"
            disabled={!file || uploading}
            style={{
              background: (!file || uploading) ? '#ccc' : '#2e7d32',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: (!file || uploading) ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {uploading ? (
              <>📤 Processing Document...</>
            ) : (
              <>📄 Upload & Process Document</>
            )}
          </button>
        </form>
      </div>
      
      <div>
        <h2>Your Documents ({Array.isArray(documents) ? documents.length : 0})</h2>
        
        {!Array.isArray(documents) || documents.length === 0 ? (
          <div style={{background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center'}}>
            <p>No documents uploaded yet.</p>
            <p style={{color: '#666', fontSize: '14px'}}>Upload your first genealogy document to get started with Gene analysis!</p>
          </div>
        ) : (
          <div style={{display: 'grid', gap: '1rem'}}>
            {documents.map(doc => (
              <div key={doc._id} style={{background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                  <div style={{flex: 1}}>
                    <h3 style={{margin: '0 0 0.5rem', color: '#2e7d32'}}>{doc.originalName || doc.filename}</h3>
                    <p style={{margin: '0.5rem 0', color: '#666', fontSize: '14px'}}>
                      Type: {doc.documentType || 'Unknown'} | 
                      Size: {doc.size ? Math.round(doc.size / 1024) + ' KB' : 'Unknown'} |
                      Uploaded: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'Unknown'}
                    </p>
                    {doc.tags && doc.tags.length > 0 && (
                      <p style={{margin: '0.5rem 0', fontSize: '12px'}}>
                        Tags: {doc.tags.join(', ')}
                      </p>
                    )}
                    {doc.processingStatus && (
                      <p style={{margin: '0.5rem 0', fontSize: '12px', color: doc.processingStatus === 'completed' ? '#28a745' : '#ffc107'}}>
                        Status: {doc.processingStatus}
                      </p>
                    )}
                    
                    {/* Image Thumbnail Preview */}
                    {isImageDocument(doc) && (
                      <div style={{marginTop: '1rem'}}>
                        <img 
                          src={`/api/documents/${doc._id}/view?token=${localStorage.getItem('token')}`}
                          alt={doc.originalName}
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleViewImage(doc)}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <p style={{fontSize: '12px', color: '#666', marginTop: '0.5rem'}}>
                          Click to view full size
                        </p>
                      </div>
                    )}
                  </div>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                    {isImageDocument(doc) && (
                      <button
                        onClick={() => handleViewImage(doc)}
                        style={{
                          background: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        View
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(doc._id, doc.originalName || doc.filename)}
                      disabled={deleting === doc._id}
                      style={{
                        background: deleting === doc._id ? '#ccc' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: deleting === doc._id ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {deleting === doc._id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Full Image Viewer Modal */}
      {viewingImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={closeImageViewer}
        >
          <div style={{position: 'relative', maxWidth: '90%', maxHeight: '90%'}}>
            <img 
              src={`/api/documents/${viewingImage._id}/view?token=${localStorage.getItem('token')}`}
              alt={viewingImage.originalName}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
            <button
              onClick={closeImageViewer}
              style={{
                position: 'absolute',
                top: '-40px',
                right: 0,
                background: 'white',
                color: 'black',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Gene Integration Section */}
      <div style={{marginTop: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6'}}>
        <h3 style={{margin: '0 0 1rem', color: '#495057', display: 'flex', alignItems: 'center'}}>
          <span style={{marginRight: '0.5rem'}}>💡</span>
          Gene Integration with Professional OCR
        </h3>
        <div style={{fontSize: '14px', color: '#6c757d', lineHeight: '1.6'}}>
          <p style={{margin: '0 0 1rem'}}>
            <strong>Professional OCR Processing:</strong> Reliable text extraction optimized for genealogical documents including:
          </p>
          <ul style={{margin: '0 0 1rem', paddingLeft: '1.5rem'}}>
            <li>Handwritten family letters and personal notes</li>
            <li>Historical photographs and documents</li>
            <li>Multi-language genealogical records</li>
            <li>Census records and vital documents</li>
            <li>Immigration and naturalization papers</li>
          </ul>
          <p style={{margin: '0'}}>
            All processed documents become available for analysis by Gene in your research conversations.
            Gene uses reliable OCR results to provide accurate genealogical insights and research guidance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadPage;
